import AppLink from 'components/AppLink/AppLink';
import classNames from 'classnames';
import { Permalink } from 'helpers';
import useTranslation from 'next-translate/useTranslation';
import { Namespace } from 'enums';
import { useHowToItems } from 'hooks';
import React, { memo, useEffect, useRef, useState } from 'react';
import styles from './index.module.scss';
import { useLang } from 'context/langContext';

let resizeTimer: number;

interface Props {
  activeIndex: number;
}

const DeviceList = ({ activeIndex = 0 }: Props): JSX.Element => {
  const { t } = useTranslation(Namespace.HowToWatch);
  const { locale } = useLang();
  const [rendered, setRendered] = useState(false);
  const [customClass, setCustomClass] = useState<string>(null);
  const [windowWidth, setWindowWidth] = useState<number>(1024);
  const refSwiper = useRef<HTMLDivElement>(null);
  const refList = useRef<HTMLUListElement>(null);

  useHowToItems(refSwiper, rendered, activeIndex, windowWidth);

  useEffect(() => {
    setRendered(true);
    setWindowWidth(window?.innerWidth);
    window.addEventListener('resize', () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        setWindowWidth(window?.innerWidth);
      }, 50);
    });

    const mouseMove = (e: MouseEvent) => {
      if (refList.current) {
        const { width, height, x, y } = refList.current.getBoundingClientRect();
        if (!(x < e.clientX && x + width > e.clientX && y < e.clientY && y + height > e.clientY)) {
          setCustomClass(styles.mouseOut);
        }
        window.addEventListener('mousemove', mouseMove, false);
      }
    };

    window.addEventListener('mousemove', mouseMove, false);
  }, []);

  const onMouseOut = (e: React.MouseEvent) => {
    e.preventDefault();
    setCustomClass(styles.mouseOut);
  };

  const onMouseEnter = (e: React.MouseEvent) => {
    e.preventDefault();
    setCustomClass(null);
  };

  return (
    <div className={styles.container}>
      <div className={classNames('swiper-container')} ref={refSwiper}>
        <ul
          className={classNames('swiper-wrapper', styles.list, customClass)}
          onMouseOut={onMouseOut}
          onMouseEnter={onMouseEnter}
          ref={refList}
        >
          <li
            className={classNames(styles.web, 'swiper-slide', activeIndex === 0 && styles.active)}
            title={t('web.title')}
          >
            <AppLink href={Permalink.howToWatchWeb(locale)}>
              <a>
                <span>{t('web.title')}</span>
              </a>
            </AppLink>
          </li>
          <li
            className={classNames(styles.smartTV, 'swiper-slide', activeIndex === 1 && styles.active)}
            title={t('smart_tv.title')}
          >
            <AppLink href={Permalink.howToWatchSmartTV(locale)}>
              <a>
                <span>{t('smart_tv.title')}</span>
              </a>
            </AppLink>
          </li>
          <li
            className={classNames(styles.mobile, 'swiper-slide', activeIndex === 2 && styles.active)}
            title={t('mobile.title')}
          >
            <AppLink href={Permalink.howToWatchMobile(locale)}>
              <a>
                <span>{t('mobile.title')}</span>
              </a>
            </AppLink>
          </li>
          <li
            className={classNames(styles.stream, 'swiper-slide', activeIndex === 3 && styles.active)}
            title={t('streaming.title')}
          >
            <AppLink href={Permalink.HowToWatchStreaming(locale)}>
              <a>
                <span>{t('streaming.title')}</span>
              </a>
            </AppLink>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default memo(DeviceList);
